"use client";

import { useState } from "react";
import { signIn } from "next-auth/react";
import { useRouter } from "next/navigation";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, Eye, EyeOff } from "lucide-react";
import Image from "next/image";
import LogoDarkMode from "../../../../public/logo-next-dark-mode.png";
import LogoLightMode from "../../../../public/logo-next-light-mode.png";

export default function LoginPage() {
    const router = useRouter();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState("");
    const [isLoading, setIsLoading] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError("");
        setIsLoading(true);

        try {
            const result = await signIn("credentials", {
                email,
                password,
                redirect: false,
            });

            if (result?.error) {
                setError("Email atau password salah");
            } else {
                router.push("/dashboard");
                router.refresh();
            }
        } catch {
            setError("Terjadi kesalahan. Silakan coba lagi.");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="h-screen flex items-center justify-center relative overflow-y-auto bg-slate-50 dark:bg-[#030712]">
            {/* Animated background */}
            <div className="absolute inset-0">
                <div className="absolute top-1/4 -left-20 w-96 h-96 bg-indigo-600/20 rounded-full blur-3xl animate-pulse" />
                <div className="absolute bottom-1/4 -right-20 w-96 h-96 bg-violet-600/20 rounded-full blur-3xl animate-pulse delay-1000" />
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-cyan-500/5 rounded-full blur-3xl" />
                {/* Grid pattern */}
                <div
                    className="absolute inset-0 opacity-[0.03]"
                    style={{
                        backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
                        backgroundSize: "60px 60px",
                    }}
                />
            </div>

            <div className="relative z-10 w-full max-w-md px-4">
                {/* Logo */}
                <div className="text-center mb-8">
                    <div className="flex flex-col items-center justify-center gap-5 mb-4">
                        <div className="flex h-10 items-center justify-center">
                            {/* Show LogoLightMode in light theme (hidden in dark) */}
                            <Image src={LogoLightMode} alt="Next IT" className="h-10 w-auto object-contain dark:hidden block" priority />
                            {/* Show LogoDarkMode in dark theme (hidden in light) */}
                            <Image src={LogoDarkMode} alt="Next IT" className="h-10 w-auto object-contain hidden dark:block" priority />
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold bg-gradient-to-r from-slate-900 dark:from-white via-slate-700 dark:via-white to-slate-400 dark:to-gray-400 bg-clip-text text-transparent tracking-tight">
                                CRM
                            </h1>
                            <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500 dark:text-gray-500 font-medium mt-1">
                                Platform Ekosistem Next IT
                            </p>
                        </div>
                    </div>
                </div>

                {/* Login Card */}
                <Card className="border-slate-200 dark:border-white/[0.08] bg-slate-100 dark:bg-white/[0.03] backdrop-blur-xl shadow-2xl">
                    <CardHeader className="text-center pb-4">
                        <CardTitle className="text-xl text-slate-900 dark:text-white">Selamat Datang</CardTitle>
                        <CardDescription className="text-slate-600 dark:text-gray-400">
                            Masuk ke dashboard CRM Anda
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="email" className="text-slate-900 dark:text-gray-300 text-sm">
                                    Email
                                </Label>
                                <Input
                                    id="email"
                                    type="email"
                                    placeholder="Email Anda"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    className="bg-slate-100 dark:bg-white/[0.05] border-slate-200 dark:border-white/[0.1] text-slate-900 dark:text-white placeholder:text-slate-500 dark:text-gray-500 focus:border-indigo-500/50 focus:ring-indigo-500/20 h-11"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="password" className="text-slate-900 dark:text-gray-300 text-sm">
                                    Password
                                </Label>
                                <div className="relative">
                                    <Input
                                        id="password"
                                        type={showPassword ? "text" : "password"}
                                        placeholder="••••••••"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        required
                                        className="bg-slate-100 dark:bg-white/[0.05] border-slate-200 dark:border-white/[0.1] text-slate-900 dark:text-white placeholder:text-slate-500 dark:text-gray-500 focus:border-indigo-500/50 focus:ring-indigo-500/20 h-11 pr-10"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                                    >
                                        {showPassword ? (
                                            <EyeOff className="h-5 w-5" />
                                        ) : (
                                            <Eye className="h-5 w-5" />
                                        )}
                                    </button>
                                </div>
                            </div>

                            {error && (
                                <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm text-center">
                                    {error}
                                </div>
                            )}

                            <Button
                                type="submit"
                                disabled={isLoading}
                                className="w-full h-11 bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-500 hover:to-violet-500 text-white font-medium shadow-lg shadow-indigo-500/25 transition-all duration-200 hover:shadow-indigo-500/40 cursor-pointer"
                            >
                                {isLoading ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        Masuk...
                                    </>
                                ) : (
                                    "Masuk"
                                )}
                            </Button>
                        </form>
                    </CardContent>
                </Card>

                <p className="text-center text-xs text-slate-300 dark:text-gray-600 mt-6">
                    © 2026 Next IT — PT Niaga Expert Teknologi
                </p>
            </div>
        </div>
    );
}
